/* eslint-disable no-restricted-syntax */
/**
 * A generic helper class for setting up checkbox dependencies.  Initialized
 * with a call to the public init(); function passing in the data
 * attribute that contains dependancy information and the page name where
 * the checkboxes exist.
 *
 * The collection of inputs must have a class that matches the data attribute
 */

/**
 * Configuration when the class is initialized
 * @private
 */
type Right = {
  index: number;
  parent: string;
  children: string[];
  required: string[];
};

const _rightNameRegex = /[^\s]*?right_/g;

// Place dependentCheckboxes in global/window scope,  can easily return
// this in the future when a module manager is being used instead.

class DependentCheckbox implements DependentCheckboxes {
  _config: {
    attr: string;
    checkboxes: JQuery<HTMLInputElement>;
    rights: {
      [key: string]: Right;
    };
  };

  constructor() {
    this._config = {
      attr: '',
      checkboxes: {} as JQuery<HTMLInputElement>,
      rights: {},
    };
  }

  /* ----------------- PUBLIC API ------------------- */

  /**
   * Initializes dependant checkboxes using the provided attr.  Will uncheck
   * and disabled any checkboxes with unchecked prerequisites.  Sets up
   * change event listeners so that inputs are enabled once all their
   * required checkboxes are checked.
   *
   * $param attr {String} - The data-attribute that holds dependancy info.
   * This input should also have a class that matches
   * this data-attribute.
   */
  init: DependentCheckboxes['init'] = (attr) => {
    this._config.attr = attr;
    this._config.checkboxes = $(`input[type="checkbox"].${attr}`);

    if (this._config.checkboxes.length) {
      this.createRightsObject();
      this.setCheckboxesGroupState();
      this.setCheckboxRequiredState();
      this.listenForChange();
    }
  };

  /**
   * Bind the init on the #view event "viewready"
   * param {String} attr - The name of the data attribute containing the required dependencies.
   */
  initFromViewEvent: DependentCheckboxes['initFromViewEvent'] = (attr) => {
    $('#view')
      .off('.view-right')
      .on('viewready.view-right', () => this.init(attr));
  };

  /* ----------------- PRIVATE API ------------------ */

  /**
   * Register event listener on all checkboxes
   */
  private listenForChange = () => {
    this._config.checkboxes.off('.view-right').on('change.view-right', (e) => {
      const right = e.currentTarget.dataset.right;

      const checked = e.currentTarget.checked;

      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      if (this._isParentRight(right)) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        this.toggleCheckedForAllChildren(right, checked);
      }

      // @ts-expect-error ts-migrate(2538) FIXME: Type 'undefined' cannot be used as an index type.
      this.setCheckboxGroupState(this._config.rights[right].parent);
      this.setCheckboxRequiredState(right, checked);
    });
  };

  /**
   * Create a global right object containing the rights information
   * to be re-used when a checkbox is checked / unchecked
   */
  private createRightsObject = () => {
    let checkbox: JQuery<HTMLInputElement>;

    for (let i = 0, ii = this._config.checkboxes.length; i < ii; ++i) {
      checkbox = this._config.checkboxes.eq(i);
      this._config.rights[checkbox.data('right')] = {
        index: i,
        parent: checkbox.data('parent-right') || false,
        children: checkbox.data('children-right') && checkbox.data('children-right').split(' '),
        required:
          checkbox.data(this._config.attr) &&
          checkbox.data(this._config.attr).replace(_rightNameRegex, '').split(' '),
      };
    }
  };

  /**
   * Set all of the parent groups to "checked" / "unchecked" / "indeterminate" states.
   */
  private setCheckboxesGroupState = () => {
    for (const right in this._config.rights) {
      // eslint-disable-next-line no-prototype-builtins
      if (!this._config.rights.hasOwnProperty(right)) continue;
      this.setCheckboxGroupState(right);
    }
  };

  /**
   * Set a target parent group to "checked" / "unchecked" / "indeterminate" states.
   * @param {String} right
   */
  private setCheckboxGroupState = (right: string) => {
    if (!right || !this._config.rights[right].children) return;
    if (!this._isGroupRight(right)) return;

    let childrenCount = 0;

    let checkedCount = 0;

    const enabledRights = this.getCheckedRights();

    let childRight;

    for (let i = 0, ii = this._config.rights[right].children.length; i < ii; ++i) {
      childRight = this._config.rights[right].children[i];
      // If the right is not in the list of rights, that means that it might be behind a module
      // and not displayed. In that case it should not affect the parent right display
      if (!this._config.rights[childRight]) continue;
      // _read rights are hidden, let's not count them!
      if (this._isReadRight(childRight)) continue;

      childrenCount++;
      if (~enabledRights.indexOf(childRight)) {
        checkedCount++;
      }
    }

    let checked = false;

    let indeterminate = false;

    if (checkedCount) {
      if (checkedCount === childrenCount) {
        checked = true;
      } else {
        indeterminate = true;
      }
    } else if (right === 'all' && enabledRights.length) {
      indeterminate = true;
    }

    this._config.checkboxes
      .eq(this._config.rights[right].index)
      .prop('checked', checked)
      .prop('indeterminate', indeterminate);

    this.setCheckboxGroupState(this._config.rights[right].parent);
  };

  /**
   * Get the list of checked rights
   * @returns {Array}
   */
  private getCheckedRights = (): Array<any> => {
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    return $.map(this._config.checkboxes, (checkbox: HTMLInputElement) => {
      if (checkbox.checked) {
        return checkbox.name.replace(_rightNameRegex, '');
      }
    });
  };

  /**
   * Check for required-rights of the checked right and disable
   * the checkbox if it doesn't meet the dependencies
   * @param {String} [checkedRight]
   * @param {Boolean} [checked]
   */
  private setCheckboxRequiredState = (checkedRight?: string, checked?: boolean) => {
    /**
     * Private function to change the checkbox state
     * @param {String} right
     * @param {Boolean} disabled
     * @param {Boolean} [checked]
     */
    const _changeCheckboxState = (right: string, disabled: boolean, checked?: boolean) => {
      this._config.checkboxes
        .eq(this._config.rights[right].index)
        .prop('disabled', disabled)
        .prop('checked', checked);
    };
    const enabledRights = this.getCheckedRights();

    let hasRequiredRights: boolean;

    const isGroupRight = checkedRight && this._isGroupRight(checkedRight);

    for (const right in this._config.rights) {
      // eslint-disable-next-line no-prototype-builtins
      if (!this._config.rights.hasOwnProperty(right)) continue;
      if (!this._config.rights[right].required) continue;
      if (
        !isGroupRight &&
        checkedRight &&
        !~this._config.rights[right].required.indexOf(checkedRight)
      )
        continue;
      // Only affect it's own children if group right is pressed
      if (isGroupRight && !~this._config.rights[checkedRight].children.indexOf(right)) continue;

      // Handle multiple level deep of dependencies
      if (typeof checked !== 'undefined' && !checked) {
        _changeCheckboxState(right, true, false);
        this.setCheckboxRequiredState(right, false);
        continue;
      }

      hasRequiredRights = true;

      for (let i = 0, ii = this._config.rights[right].required.length; i < ii; ++i) {
        if (!~enabledRights.indexOf(this._config.rights[right].required[i])) {
          hasRequiredRights = false;
          _changeCheckboxState(right, true, false);
          break;
        }
      }

      if (hasRequiredRights) {
        _changeCheckboxState(right, false);
      }
    }
  };

  /**
   * When a parent is clicked, toggle all of it's children
   * @param {String} right
   * @param {Boolean} checked
   */
  private toggleCheckedForAllChildren = (right: string, checked: boolean) => {
    const children = this._config.rights[right] && this._config.rights[right].children;
    if (!children) return;

    if (checked) {
      this._config.checkboxes.eq(this._config.rights[right].index).prop('indeterminate', false);
    }

    for (let i = 0, ii = children.length; i < ii; ++i) {
      // The right might not be displayed on the page
      if (!this._config.rights[children[i]]) continue;

      this._config.checkboxes
        .eq(this._config.rights[children[i]].index)
        .prop('indeterminate', false)
        .prop('checked', checked)
        .prop('disabled', checked ? false : undefined);

      this.toggleCheckedForAllChildren(children[i], checked);
    }
  };

  /**
   * Helper returns if a right is a group right
   * @param {String} right
   * @returns {boolean}
   * @private
   */
  private _isGroupRight = (right: string): boolean => {
    return /^(?:inventory|.*?all)$/.test(right);
  };

  /**
   * Helper returns if a right is a parent right
   * @param {String} right
   * @returns {boolean}
   * @private
   */
  private _isParentRight = (right: string): boolean | string[] => {
    return this._config.rights[right] && this._config.rights[right].children;
  };

  /**
   * Helper returns if a right is a read right
   * @param {String} right
   * @returns {boolean}
   * @private
   */
  private _isReadRight = (right: string): boolean => {
    return /_read$/.test(right);
  };
}

window.dependentCheckboxes = new DependentCheckbox();
